import React, { useCallback, useState } from "react";
import { Field, FormikErrors, useFormikContext } from "formik";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ReceiptIcon from "@mui/icons-material/Receipt";
import EditIcon from "@mui/icons-material/Edit";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import PhoneIcon from "@mui/icons-material/Phone";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NightlightIcon from "@mui/icons-material/Nightlight";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import {
  BaseExpenseFieldsProps,
  ExpenseEntry,
  ExpenseFormData,
  GENERIC_EXPENSE_CATEGORIES,
  GenericExpenseCategory,
} from "./types";
import { formatInputCurrency } from "../../../utils/utils";
import {
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../../utils/styles";
import { Button, Checkbox, FormControlLabel } from "@mui/material";

// Category icons mapping
const getCategoryIcon = (category: GenericExpenseCategory) => {
  switch (category) {
    case "TOLL":
    case "RTO":
    case "Challan":
      return <DirectionsCarIcon fontSize="small" />;
    case "Food Allowance":
      return <RestaurantIcon fontSize="small" />;
    case "Mobile Recharge":
      return <PhoneIcon fontSize="small" />;
    case "Loading/Unloading Fees":
      return <LocalShippingIcon fontSize="small" />;
    case "Night Halt Charges":
      return <NightlightIcon fontSize="small" />;
    case "Emergency Fund Used":
      return <AccountBalanceWalletIcon fontSize="small" />;
    case "Dhobi":
    case "Bus Cleaning (Interior)":
      return <CleaningServicesIcon fontSize="small" />;
    case "Custom":
      return <EditIcon fontSize="small" />;
    default:
      return <ReceiptIcon fontSize="small" />;
  }
};

function GenericExpenseFields({
  index,
  isProjectExpense = false,
  isTripExpense = false,
}: BaseExpenseFieldsProps) {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<ExpenseFormData>();
  const currentCategory =
    (values.expenses[index]?.category as GenericExpenseCategory) ||
    (values.expenses[index]?.type as GenericExpenseCategory);
  const expenseErrors = errors.expenses?.[index] as
    | FormikErrors<ExpenseEntry>
    | undefined;
  const handleCategoryChange = (category: GenericExpenseCategory) => {
    //set isEdited to true
    setFieldValue(`expenses.${index}.isEdited`, true);
    setFieldValue(`expenses.${index}.category`, category);
    if (category !== "Custom") {
      setFieldValue(`expenses.${index}.customCategory`, "");
    }
  };
  const [addNotes, setAddNotes] = useState<boolean>(false);

  const handleMarkAsPaid = useCallback(
    (isPaid: boolean) => {
      if (isPaid) {
        setFieldValue(`expenses.${index}.payments`, [
          ...values.expenses[index].payments!,
          {
            type: "ADVANCE",
            mode: "CASH",
            date: values.expenses[index].expenseDate || "",
            location: values.expenses[index].location || "",
            receivedFrom: values.expenses[index].paidTo || "",
            remarks: "",
            status: "PAID",
            isEdited: false,
            isDeleted: false,
            isNew: true,
            amount: values.expenses[index].amount,
            masterExpenseId: values.expenses[index].id || null,
          },
        ]);
      } else {
        setFieldValue(`expenses.${index}.payments`, []);
      }
    },
    [index, setFieldValue, values.expenses]
  );

  return (
    <Grid container item xs={9} spacing={1}>
      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Date"
          type="date"
          name={`expenses.${index}.expenseDate`}
          fullWidth
          required
          {...commonDateFieldProps}
        />
      </Grid>

      {!isTripExpense && (
        <Grid item xs={12} sm={3}>
          <Field
            as={TextField}
            select
            label="Category"
            name={`expenses.${index}.category`}
            fullWidth
            required
            value={currentCategory || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleCategoryChange(e.target.value as GenericExpenseCategory)
            }
            {...commonTextFieldProps}
          >
            {GENERIC_EXPENSE_CATEGORIES.map((category) => (
              <MenuItem key={category} value={category}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    py: 0.25,
                  }}
                >
                  {getCategoryIcon(category)}
                  {category}
                </Box>
              </MenuItem>
            ))}
          </Field>
        </Grid>
      )}

      {currentCategory === "Custom" && (
        <Grid item xs={3}>
          <Field
            as={TextField}
            label="Custom Category Name"
            name={`expenses.${index}.customCategory`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.customCategory`, e.target.value);
              setFieldValue(`expenses.${index}.isEdited`, true);
            }}
            error={Boolean(
              expenseErrors?.customCategory &&
                touched.expenses?.[index]?.customCategory
            )}
            helperText={
              expenseErrors?.customCategory &&
              touched.expenses?.[index]?.customCategory
                ? expenseErrors?.customCategory
                : ""
            }
            fullWidth
            required
            placeholder="Enter your custom category name..."
            {...commonTextFieldProps}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Paid To"
          name={`expenses.${index}.paidTo`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.paidTo`, e.target.value);
            setFieldValue(`expenses.${index}.isEdited`, true);
          }}
          error={Boolean(
            expenseErrors?.paidTo && touched.expenses?.[index]?.paidTo
          )}
          helperText={
            expenseErrors?.paidTo && touched.expenses?.[index]?.paidTo
              ? expenseErrors?.paidTo
              : null
          }
          fullWidth
          required
          placeholder="Enter payee name"
          {...commonTextFieldProps}
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field name={`expenses.${index}.amount`}>
          {({ field, meta }: any) => (
            <TextField
              {...field}
              {...commonTextFieldProps}
              label="Amount"
              fullWidth
              required
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error}
              value={field.value ? formatInputCurrency(field.value) : ""}
              onChange={(e) => {
                const value = e.target.value.replace(/,/g, "");
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  field.onChange({
                    target: {
                      name: field.name,
                      value: value,
                    },
                  });
                  setFieldValue(`expenses.${index}.isEdited`, true);
                }
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    component="span"
                    sx={{ mr: 0.5, fontSize: "0.75rem" }}
                  >
                    ₹
                  </Typography>
                ),
              }}
              inputProps={{
                inputMode: "decimal",
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Contact Number"
          name={`expenses.${index}.contactNumber`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.contactNumber`, e.target.value);
            setFieldValue(`expenses.${index}.isEdited`, true);
          }}
          fullWidth
          placeholder="Enter contact number (optional)"
          {...commonTextFieldProps}
          inputProps={{
            maxLength: 10,
          }}
          error={Boolean(
            expenseErrors?.contactNumber && expenseErrors?.contactNumber
          )}
          helperText={
            expenseErrors?.contactNumber && expenseErrors?.contactNumber
          }
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Location"
          name={`expenses.${index}.location`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.location`, e.target.value);
            setFieldValue(`expenses.${index}.isEdited`, true);
          }}
          fullWidth
          placeholder="Enter location"
          {...commonTextFieldProps}
        />
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={() => setAddNotes(!addNotes)}
          size={smallButtonProp.size}
          sx={{
            ...smallButtonProp.sx,
            color: "grey",
            borderColor: "grey.400",
            "&:hover": {
              borderColor: "grey.400",
              color: "grey",
            },
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
            {addNotes ? "Hide Notes" : "Add Notes"}
          </Typography>
        </Button>
        {Number(values.expenses[index].amount) > 0 &&
          values.expenses[index].payments?.length === 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    values.expenses[index].payments?.length! &&
                    values.expenses[index].payments?.length! > 0
                      ? true
                      : false
                  }
                  onChange={(e) => {
                    handleMarkAsPaid(e.target.checked);
                  }}
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: "0.7rem" },
                  }}
                />
              }
              label="Mark as Paid"
              sx={{
                p: 0,
                pr: 1,
                ml: 0.75,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.7rem",
                  color: "primary.main",
                },
              }}
            />
          )}
      </Grid>
      {addNotes && (
        <Grid item xs={12}>
          <Field
            as={TextField}
            label="Notes"
            name={`expenses.${index}.notes`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.notes`, e.target.value);
              setFieldValue(`expenses.${index}.isEdited`, true);
            }}
            fullWidth
            multiline
            rows={2}
            placeholder="Any additional notes about this expense..."
            {...commonTextFieldProps}
          />
        </Grid>
      )}
    </Grid>
  );
}

export default GenericExpenseFields;
